import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { serviceLogger } from '../../utils/serviceLogger';
import Logo from './Logo';
import LogoBlue from '../../assets/images/Logo-Blue.png';
import MobileMenu from './Menu/MobileMenu';
import NavigationMenu from './NavigationMenu';
import DesktopMenu from './Menu/DesktopMenu';
import BackButton from './BackButton';

/**
 * Header Component
 *
 * This component renders the main application header, which includes:
 * - The application logo
 * - A back button for navigation (conditionally rendered)
 * - A user icon or a loading skeleton based on the loading context
 * - A mobile menu trigger for smaller screens
 *
 * @param {number} [goBackTo=-1] - Steps to go back when the Back button is clicked.
 * @param {boolean} [hideBackButton=false] - When set to true, the back button will not be rendered.
 * @param {boolean} [hideMenu=false] - When set to true, the hamburger menu will not be rendered.
 */
const Header = ({ goBackTo = -1, hideBackButton = false, hideMenu = false }) => {
  const navigate = useNavigate();
  const [isBackLoading, setIsBackLoading] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const handleBack = async () => {
    setIsBackLoading(true);
    try {
      navigate(goBackTo);
    } catch (error) {
      serviceLogger.error('Error navigating back:', error);
    } finally {
      setIsBackLoading(false);
    }
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  return (
    <header className="fixed shadow-sm top-0 left-0 right-0 z-30 flex items-center pt-5 px-4 md:px-12 lg:px-20 w-full gap-4 sm:gap-6 md:gap-8 lg:gap-2 flex-row md:justify-between bg-custom-light-gray">
      <div className="flex items-center flex-row w-full">
        {/* Main Container for Mobile */}
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center w-full">
            {/* Mobile Distribution */}
            {isMobile ? (
              <div className="flex items-center justify-between w-full">
                {/* Back Button */}
                {!hideBackButton && (
                  <div className="flex-1 flex">
                    <BackButton
                      onClick={handleBack}
                      disabled={isBackLoading}
                      isLoading={isBackLoading}
                    />
                  </div>
                )}

                {/* Logo */}
                <div className="flex items-center justify-center mx-2 min-w-[120px] md:min-w-[200px]">
                  <Logo URL={LogoBlue} name="Logo Blue" variant="small" />
                </div>

                {/* Mobile: Menu */}
                {!hideMenu && (
                  <div className="flex-1 flex justify-end items-center gap-2">
                    <NavigationMenu menuIsOpen={isMenuOpen} toggleMenu={toggleMenu} />
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Distribution */
              <>
                <div className="flex items-center gap-4">
                  <div className="flex items-center min-w-[200px] max-w-[220px]">
                    <Logo URL={LogoBlue} name="Logo Blue" variant="small" />
                  </div>
                </div>

                {!hideBackButton && (
                  <div className="w-full flex flex-col items-center justify-center gap-4 sm:w-auto sm:mt-0 sm:flex-row md:gap-4 lg:gap-6 md:ml-3 lg:mb-5">
                    <BackButton
                      onClick={handleBack}
                      disabled={isBackLoading}
                      isLoading={isBackLoading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* User Icon for Desktop */}
      {!isMobile && !hideMenu && (
        <div className="hidden lg:flex items-center justify-end gap-4 w-full lg:w-auto mt-4 p-4 lg:mt-0">
          <NavigationMenu menuIsOpen={isMenuOpen} toggleMenu={toggleMenu} />
        </div>
      )}
      {!hideMenu && (isMobile ? (
        <MobileMenu isOpen={isMenuOpen} onClose={toggleMenu} />
      ) : (
        <DesktopMenu onClose={toggleMenu} showMenu={isMenuOpen} />
      ))}
    </header>
  );
};

export default Header;
